import { useState } from 'react'

type Props = {
  /** O nome do produto. Vira a inicial quando não há foto. */
  nome: string
  /** A foto, quando existe. */
  src: string | null
  /** Tamanho e forma da caixa — decisão de quem hospeda. Ver abaixo. */
  className?: string
  /**
   * `cobrir` preenche a caixa e corta o que sobra; `conter` mostra a embalagem
   * inteira sobre o fundo. Miniatura de lista quer a primeira; retrato quer a
   * segunda.
   */
  ajuste?: 'cobrir' | 'conter'
  /**
   * O texto alternativo. **Vazio por padrão, e quase sempre é o certo.**
   *
   * Esta miniatura costuma aparecer coladinha ao nome do produto, e um leitor
   * de tela que anunciasse "foto de Coca-Cola 2L" antes de "Coca-Cola 2L"
   * diria a mesma coisa duas vezes. A exceção é a seção "Foto" da ficha, onde
   * a imagem é o conteúdo da seção e não o enfeite de um nome — lá ela se
   * descreve, como em `FotoAmpliada`.
   */
  alt?: string
}

/**
 * A foto de um produto — **e o lugar dela quando não há foto**.
 *
 * ## Havia cinco respostas para "não tem foto", e três eram "nada"
 *
 * A lista de Embalagens desenhava caixa tracejada com a inicial. O baralho dos
 * grupos desenhava outra, com medidas próprias. E a lista de variantes de um
 * grupo, o herói da página do produto e a ficha simplesmente **não
 * renderizavam o `<img>`** — o nome saltava para a esquerda, as linhas
 * desalinhavam e o herói mudava de layout conforme o produto tivesse foto ou
 * não. É o mesmo defeito que as decisões 225 e 226 corrigiram nas outras duas
 * listas, sobrevivendo nas que ninguém tinha olhado.
 *
 * Na base da casa **quase nenhum produto tem imagem**, então o caso sem foto é
 * o comum, não a exceção.
 *
 * ## A inicial não distingue, e não é para isso que ela serve
 *
 * Numa lista ordenada por nome, "Coca-Cola 2L", "Coca-Cola 600ml" e
 * "Coca-Cola lata" mostram três "C" seguidos. Considerei trocá-la por uma
 * silhueta escolhida pela unidade (garrafa para `ml`, pacote para `g`) e o
 * problema é o mesmo: as três Cocas são todas garrafas. Quem distingue as
 * linhas é o texto delas, que já está ali. O trabalho da inicial é outro —
 * dizer *"não há foto"* sem parecer quebrado, que é o que a decisão 225 pediu.
 *
 * ## Imagem que não carrega cai de volta no lugar
 *
 * Nenhuma das cinco tratava `onError`, e uma URL pode morrer sem ninguém
 * mexer: objeto apagado do Storage, foto importada da base pública cujo
 * caminho mudou. O resultado hoje seria o ícone de imagem quebrada do
 * navegador dentro do cartão. Aqui ela volta a ser a caixa com a inicial, que
 * é a mesma coisa que a ausência — porque, para quem olha, é.
 *
 * ## O fundo sai dos tokens, e isso não é purismo
 *
 * A versão da lista trazia `linear-gradient(145deg,#2a323d,#161b23)` escrito à
 * mão. Os dois tons não existem na paleta: ela é amostrada de uma foto e as
 * distâncias entre as cores foram **medidas** (ver `tokens.css`). Hex solto
 * não participa dessa conta e não acompanha nenhuma mudança de tema.
 *
 * ## Onde ela entra, e onde NÃO entra
 *
 * Ela reserva espaço onde o **layout depende disso**: as três listas e o herói
 * da página do produto. Não entra nos dois controles de foto — a seção "Foto"
 * da ficha e o `EscolherFoto` do cadastro —, porque ali quem chegou veio
 * justamente trocar a imagem e o botão já diz que ela não existe; a moldura
 * vazia seria altura sem informação. A seção da ficha usa a `Miniatura` só
 * quando **há** foto, pelo `onError`.
 *
 * ## O tamanho é decisão de quem chama
 *
 * Pelo motivo da decisão 229(d): `PilhaDeFotos` trazia `w-1/3` embutido, o
 * `<Link>` que passou a envolvê-lo também era `w-1/3`, e a carta desabou para
 * 22 px sem nada reclamar. Largura de caixa é do contexto, não do desenho que
 * a preenche.
 */
export function Miniatura({ nome, src, className = '', ajuste = 'cobrir', alt = '' }: Props) {
  /**
   * Guarda **qual** URL falhou, e não um booleano.
   *
   * Trocar de produto sem limpar isto deixaria a foto nova escondida atrás da
   * falha da anterior — a lista reusa o componente por posição, não por id. A
   * saída óbvia seria um `useEffect` limpando quando `src` muda, e o lint
   * proíbe (`react-hooks/set-state-in-effect`) **pelo motivo certo**: com o
   * reset num efeito, a foto nova aparece quebrada por um render antes de o
   * efeito rodar. É o mesmo conserto do `useLive` na decisão 168(f) — guardar
   * para qual valor o estado vale descarta o antigo na hora, sem efeito
   * nenhum.
   */
  const [falhouEm, setFalhouEm] = useState<string | null>(null)
  const falhou = falhouEm !== null && falhouEm === src

  const caixa =
    'grid shrink-0 place-items-center overflow-hidden rounded-[var(--radius-md)] ' +
    'bg-[linear-gradient(145deg,var(--surface),var(--surface-sunken))]'

  if (src === null || falhou) {
    // Sempre decorativa: a caixa com a inicial não é informação, é o lugar da
    // foto. Anunciar "F" a quem não enxerga não diz nada que a ausência de
    // imagem já não diga.
    return (
      <span aria-hidden="true" className={`${caixa} ${className}`.trim()}>
        <span
          className="grid h-full w-full place-items-center rounded-[var(--radius-md)]
                     border border-dashed border-[var(--border)]
                     font-[var(--font-display)] text-[var(--text-faint)]"
        >
          {nome.slice(0, 1).toUpperCase()}
        </span>
      </span>
    )
  }

  return (
    // `aria-hidden` acompanha o `alt`: escondendo a caixa sempre, um `alt` de
    // verdade nunca chegaria a ser lido — e seria um texto escrito para
    // ninguém.
    <span aria-hidden={alt === '' ? true : undefined} className={`${caixa} ${className}`.trim()}>
      <img
        src={src}
        alt={alt}
        onError={() => {
          setFalhouEm(src)
        }}
        className={`h-full w-full ${ajuste === 'conter' ? 'object-contain' : 'object-cover'}`}
      />
    </span>
  )
}
